import { useEffect, useState } from 'react';
import { disclaimerAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { renderDisclaimerBody } from './disclaimerContent';

// Bilingual copy. Keep both languages in one place so a translation
// update touches a single object.
const COPY = {
  en: {
    title: 'Confidentiality & Use Acknowledgment',
    subtitle: (days) => `You must review and agree every ${days} days to continue using KR Agency Hub.`,
    intro: (
      <>
        All content, materials, training videos, marketing collateral, logos,
        branding, forms, sample files, pricing information, and any other
        resources available on this platform are <strong>confidential and
        proprietary</strong> to KR Enterprise Group and are provided for the
        <strong> exclusive use of contracted agents and agencies</strong> in
        connection with their authorized work with KR.
      </>
    ),
    prohibited: 'You agree that you will NOT:',
    bullets: [
      'Share, distribute, resell, or republish any material outside the platform.',
      'Alter, modify, edit, deface, remove attribution from, or create derivative works of any logo, artwork, video, document, or template — including changing colors, copy, layout, or branding.',
      'Use any material for any purpose other than legitimate KR-affiliated agent activity (recruiting, training, member education, marketing of KR-approved products).',
      'Reverse-engineer, screen-scrape, or export bulk data from the platform.',
    ],
    legal: (
      <>
        All materials are protected by <strong>federal copyright law</strong> and
        KR retains all rights, title, and interest in the intellectual property.
        Unauthorized use, alteration, or misappropriation of any content may
        result in immediate termination of your access and may expose you and
        your agency to <strong>civil and criminal liability under the laws of
        the State of Florida </strong> and applicable federal statutes,
        including but not limited to actions for copyright infringement
        (17 U.S.C. § 501), misappropriation of trade secrets (Fla. Stat. § 688),
        and civil theft (Fla. Stat. § 772.11).
      </>
    ),
    closing: (days) => (
      <>
        By clicking <strong>“I have read and agree”</strong> below, you
        acknowledge you have read these terms and agree to be bound by them for
        the next {days} days, at which point you will be asked to re-confirm.
      </>
    ),
    checkboxLabel: 'I have read, understood, and agree to the confidentiality and use terms above.',
    signedNameLabel: 'Type your full name (required — this is your legal signature)',
    signedNameMissing: 'Please type your full name to sign the disclaimer.',
    acceptBtn: 'I have read and agree',
    langPill: 'ES',
  },
  es: {
    title: 'Reconocimiento de Confidencialidad y Uso',
    subtitle: (days) => `Debes revisar y aceptar cada ${days} días para seguir usando KR Agency Hub.`,
    intro: (
      <>
        Todo el contenido, los materiales, los videos de entrenamiento, el
        material de mercadeo, los logotipos, la identidad de marca, los
        formularios, los archivos de muestra, la información de precios y
        cualquier otro recurso disponible en esta plataforma son{' '}
        <strong>confidenciales y propiedad</strong> de KR Enterprise Group y se
        proveen para el <strong>uso exclusivo de los agentes y agencias
        contratados</strong> en el marco de su trabajo autorizado con KR.
      </>
    ),
    prohibited: 'Aceptas que NO harás lo siguiente:',
    bullets: [
      'Compartir, distribuir, revender ni publicar ningún material fuera de la plataforma.',
      'Alterar, modificar, editar, dañar, quitar la autoría, ni crear obras derivadas de ningún logotipo, arte, video, documento o plantilla — incluyendo cambios de color, texto, diseño o identidad de marca.',
      'Usar cualquier material para fines distintos a la actividad legítima de agente afiliado a KR (reclutamiento, entrenamiento, educación de miembros, mercadeo de productos aprobados por KR).',
      'Hacer ingeniería inversa, extracción masiva de datos ni copiar contenido de la plataforma.',
    ],
    legal: (
      <>
        Todos los materiales están protegidos por la <strong>ley federal de
        derechos de autor</strong> y KR conserva todos los derechos, títulos e
        intereses sobre la propiedad intelectual. El uso no autorizado, la
        alteración o apropiación indebida de cualquier contenido puede resultar
        en la <strong>terminación inmediata de tu acceso</strong> y puede
        exponerte a ti y a tu agencia a <strong>responsabilidad civil y
        criminal bajo las leyes del Estado de Florida</strong> y estatutos
        federales aplicables, incluyendo pero no limitado a acciones por
        infracción de derechos de autor (17 U.S.C. § 501), apropiación
        indebida de secretos comerciales (Fla. Stat. § 688) y hurto civil
        (Fla. Stat. § 772.11).
      </>
    ),
    closing: (days) => (
      <>
        Al hacer clic en <strong>“He leído y acepto”</strong>, reconoces que
        has leído estos términos y aceptas quedar obligado por ellos durante
        los próximos {days} días, momento en el cual se te pedirá reconfirmar.
      </>
    ),
    checkboxLabel: 'He leído, entendido y acepto los términos de confidencialidad y uso descritos arriba.',
    signedNameLabel: 'Escribe tu nombre completo (obligatorio — esta es tu firma legal)',
    signedNameMissing: 'Por favor escribe tu nombre completo para firmar el disclaimer.',
    acceptBtn: 'He leído y acepto',
    langPill: 'EN',
  },
};

/**
 * DisclaimerGate — Confidentiality / copyright acknowledgment popup.
 *
 * Any external user (Agent, Agency_Principal, Agency_User) sees this
 * blocking modal at most once every 5 days. They must tick the
 * checkbox and press "I agree" before the app is usable. Language can
 * be flipped between EN and ES with the pill in the header.
 */
export default function DisclaimerGate({ user }) {
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [meta, setMeta] = useState({ version: '1.0', interval_days: 5, content: null });
  const [checked, setChecked] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Persist the last language pick per browser so bilingual agents
  // do not have to switch every time.
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('kr_disclaimer_lang') === 'es' ? 'es' : 'en'; } catch { return 'en'; }
  });
  const t = COPY[lang];
  const toggleLang = () => {
    const next = lang === 'en' ? 'es' : 'en';
    setLang(next);
    try { localStorage.setItem('kr_disclaimer_lang', next); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user?.user_id) return;
    // Do NOT show the disclaimer while the user still owes a
    // temporary-password rotation. Two blocking modals stacked on
    // top of each other made it impossible to focus either input.
    // Once ForcePasswordChangeModal completes and AuthContext
    // refreshes the user, this effect re-runs and we fetch status.
    if (user?.force_password_change) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await disclaimerAPI.status();
        if (cancelled) return;
        if (r.data?.needs_acceptance) {
          setNeedsAcceptance(true);
          setMeta({
            version: r.data.version || '1.0',
            interval_days: r.data.interval_days || 5,
            content: r.data.content || null,
          });
        }
      } catch {
        // Fail-open — if the check fails we do not block the app.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, user?.force_password_change]);

  const trimmedName = signedName.trim();
  const canSubmit = checked && trimmedName.length > 0;

  const accept = async () => {
    if (!canSubmit) {
      if (!trimmedName) toast.error(t.signedNameMissing);
      return;
    }
    setSubmitting(true);
    try {
      await disclaimerAPI.accept(meta.version, trimmedName);
      setNeedsAcceptance(false);
      toast.success(lang === 'es' ? 'Gracias — aceptación registrada' : 'Thanks — acknowledgment recorded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === 'es' ? 'No se pudo registrar. Intenta de nuevo.' : 'Could not record acceptance. Please try again.'));
    } finally { setSubmitting(false); }
  };

  if (!needsAcceptance) return null;
  // Extra safety: never show while the password-change modal is up.
  if (user?.force_password_change) return null;

  const customTitle = (lang === 'es' ? meta.content?.title_es : meta.content?.title_en) || null;
  const customBody = (lang === 'es' ? meta.content?.body_es : meta.content?.body_en) || null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="disclaimer-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-2xl overflow-hidden">
        <div className="flex items-start gap-3 bg-amber-50 border-b border-amber-200 px-6 py-4">
          <ShieldAlert className="text-amber-600 shrink-0 mt-0.5" size={22} />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-900">{customTitle || t.title}</h2>
            <p className="text-xs text-slate-600 mt-0.5">{t.subtitle(meta.interval_days)}</p>
          </div>
          <button
            type="button"
            onClick={toggleLang}
            data-testid="disclaimer-lang-toggle"
            className="shrink-0 rounded-full border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 text-[11px] font-semibold px-3 py-1 uppercase tracking-wide"
            aria-label="Toggle language"
          >
            {t.langPill}
          </button>
        </div>
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto text-sm text-slate-700 space-y-3" data-testid="disclaimer-body">
          {customBody ? (
            renderDisclaimerBody(customBody)
          ) : (
            <>
              <p>{t.intro}</p>
              <p>{t.prohibited}</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                {t.bullets.map((b, i) => (<li key={i}>{b}</li>))}
              </ul>
              <p>{t.legal}</p>
              <p>{t.closing(meta.interval_days)}</p>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 space-y-3">
          <label className="flex items-start gap-2 cursor-pointer" data-testid="disclaimer-agree-row">
            <Checkbox
              checked={checked}
              onCheckedChange={(c) => setChecked(!!c)}
              data-testid="disclaimer-agree-checkbox"
              className="mt-0.5"
            />
            <span className="text-sm text-slate-800">{t.checkboxLabel}</span>
          </label>
          <div>
            <input
              type="text"
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              placeholder={t.signedNameLabel}
              required
              aria-required="true"
              className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400"
              data-testid="disclaimer-signed-name"
              autoComplete="off"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={accept}
              disabled={!canSubmit || submitting}
              data-testid="disclaimer-accept-btn"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {submitting && <Loader2 size={14} className="mr-2 animate-spin" />}
              {t.acceptBtn}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
